import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip, Slider,
  useTheme, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MapView from '../MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { readFileAsText, parseAnyGeoFile, haversineDistance, calculateSpeed } from '../../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../../components/SEO';
import L from 'leaflet';

function SpeedAnomalyDetection() {
  const theme = useTheme();
  const [features, setFeatures] = useState([]);
  const [error, setError] = useState('');
  const [maxSpeed, setMaxSpeed] = useState(200);
  const [spikeThreshold, setSpikeThreshold] = useState(50);
  const [zeroDuration, setZeroDuration] = useState(30);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(600);

  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(() => setChartWidth(containerRef.current?.clientWidth - 16 || 600));
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [features]);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse file');
      const fc = result.geoJson;
      let pts = fc.features.filter(f => f.geometry?.type === 'Point');
      if (pts.length < 2) {
        const lines = fc.features.filter(f => f.geometry?.type === 'LineString');
        if (lines.length > 0) pts = lines[0].geometry.coordinates.map((c, i) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: { index: i } }));
      }
      if (pts.length < 2) throw new Error('Need Point features with speed/timestamp');
      setFeatures(pts);
      setError('');
    } catch (err) { setError(err.message); }
  }, []);

  const speeds = useMemo(() => {
    return features.map((f, i) => {
      let speed = f.properties?.speed ?? f.properties?.Speed ?? f.properties?.velocity;
      if (speed === undefined && i > 0) {
        const prevTs = features[i - 1].properties?.timestamp || features[i - 1].properties?.time;
        const currTs = f.properties?.timestamp || f.properties?.time;
        if (prevTs && currTs) {
          speed = calculateSpeed(features[i - 1].geometry.coordinates, f.geometry.coordinates, prevTs, currTs);
        }
      }
      return speed ?? 0;
    });
  }, [features]);

  const anomalies = useMemo(() => {
    if (speeds.length < 2) return [];
    const anomalyList = [];

    for (let i = 0; i < speeds.length; i++) {
      // Impossibly high speed
      if (speeds[i] > maxSpeed) {
        anomalyList.push({
          type: 'high_speed', index: i, speed: speeds[i],
          reason: `Speed ${speeds[i].toFixed(1)} km/h exceeds ${maxSpeed} km/h limit`,
          coordinate: features[i].geometry.coordinates,
          severity: 'high',
        });
      }

      // Speed spike (sudden jump)
      if (i > 0) {
        const diff = Math.abs(speeds[i] - speeds[i - 1]);
        if (diff > spikeThreshold) {
          anomalyList.push({
            type: 'spike', index: i, speed: speeds[i],
            reason: `Speed jumped ${diff.toFixed(1)} km/h (${speeds[i - 1].toFixed(0)} → ${speeds[i].toFixed(0)})`,
            coordinate: features[i].geometry.coordinates,
            severity: diff > spikeThreshold * 2 ? 'high' : 'medium',
          });
        }
      }

      // Stuck at zero (possible signal loss)
      if (speeds[i] === 0 && i > 0 && speeds[i - 1] === 0) {
        const prevTs = features[i - 1].properties?.timestamp || features[i - 1].properties?.time;
        const currTs = features[i].properties?.timestamp || features[i].properties?.time;
        if (prevTs && currTs) {
          const dt = (new Date(currTs) - new Date(prevTs)) / 1000;
          const dist = haversineDistance(features[i - 1].geometry.coordinates, features[i].geometry.coordinates);
          if (dt > zeroDuration && dist > 50) {
            anomalyList.push({
              type: 'zero_movement', index: i, speed: 0,
              reason: `Zero speed for ${dt.toFixed(0)}s but moved ${dist.toFixed(0)}m`,
              coordinate: features[i].geometry.coordinates,
              severity: 'medium',
            });
          }
        }
      }
    }
    return anomalyList;
  }, [speeds, features, maxSpeed, spikeThreshold, zeroDuration]);

  // Draw speed chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || speeds.length === 0) return;
    const ctx = canvas.getContext('2d');
    const h = 140;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = chartWidth * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const pad = { top: 15, right: 10, bottom: 20, left: 40 };
    const cw = chartWidth - pad.left - pad.right;
    const ch = h - pad.top - pad.bottom;

    ctx.fillStyle = theme.palette.mode === 'dark' ? '#1a1a2e' : '#f8fafc';
    ctx.fillRect(0, 0, chartWidth, h);

    const maxVal = Math.max(...speeds, maxSpeed, 1);
    const anomalyIndices = new Set(anomalies.map(a => a.index));

    // Speed line
    ctx.beginPath();
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 1.5;
    speeds.forEach((s, i) => {
      const x = pad.left + (i / (speeds.length - 1)) * cw;
      const y = pad.top + ch - (s / maxVal) * ch;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Max speed line
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    const maxY = pad.top + ch - (maxSpeed / maxVal) * ch;
    ctx.beginPath(); ctx.moveTo(pad.left, maxY); ctx.lineTo(pad.left + cw, maxY); ctx.stroke();
    ctx.setLineDash([]);

    // Anomaly dots
    anomalies.forEach(a => {
      const x = pad.left + (a.index / (speeds.length - 1)) * cw;
      const y = pad.top + ch - (a.speed / maxVal) * ch;
      ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = a.severity === 'high' ? '#ef4444' : '#f59e0b';
      ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
    });

    // Y-axis labels
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#888' : '#999';
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 3; i++) {
      const val = (maxVal * (3 - i) / 3);
      const y = pad.top + (ch * i / 3);
      ctx.fillText(val.toFixed(0), pad.left - 5, y + 3);
    }
  }, [speeds, anomalies, chartWidth, maxSpeed, theme.palette.mode]);

  const mapGeoJson = useMemo(() => {
    if (features.length === 0) return null;
    const f = [];
    const coords = features.map(ft => ft.geometry.coordinates);
    if (coords.length > 1) f.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: { layer: 'route' } });
    anomalies.forEach(a => {
      f.push({ type: 'Feature', geometry: { type: 'Point', coordinates: a.coordinate }, properties: { layer: 'anomaly', ...a } });
    });
    return { type: 'FeatureCollection', features: f };
  }, [features, anomalies]);

  const styleFunction = useCallback((feature) => {
    if (feature.properties.layer === 'route') return { color: '#6366f1', weight: 2.5, opacity: 0.5 };
    return {};
  }, []);

  const pointToLayerFn = useCallback((feature, latlng) => {
    if (feature.properties.layer === 'anomaly') {
      const sev = feature.properties.severity;
      return L.circleMarker(latlng, {
        radius: 7, fillColor: sev === 'high' ? '#ef4444' : '#f59e0b',
        color: '#fff', weight: 2, fillOpacity: 0.9,
      }).bindTooltip(feature.properties.reason);
    }
    return L.circleMarker(latlng, { radius: 0 });
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Speed Anomaly Detection | Telematics Analyzer | UmbrellaCo"
        description="Detect speeding, sudden spikes, and stationary data loss anomalies in your GPS telematics traces. Free online mapping and analysis tool."
        keywords="speed anomaly detection, gps speeding, telematics data analysis, detect stationary gps, speed limits map"
        url="/spatial-tools/speed-anomalies"
        jsonLd={createSoftwareAppSchema(
          'Speed Anomaly Detection',
          'Detect and visualize speed anomalies, spikes, and data loss in GPS traces.',
          'https://umbrellaco.in/spatial-tools/speed-anomalies',
          ['Speed Detection', 'Anomaly Visualization', 'Telematics Analysis']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Speed Anomaly Detection</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload GPS traces to detect speeding over custom limits, sudden velocity spikes, and unexplained stationary points.
        </Typography>
      </Box>
      <PanelGroup direction="vertical">
        <Panel defaultSize={70} minSize={40}>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={30} minSize={20}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ p: 2 }}>
                  <Button component="label" variant="contained" fullWidth size="small" startIcon={<UploadFileIcon />}
                    sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}>
                    {features.length > 0 ? `Loaded (${features.length} pts)` : 'Upload Trip Data'}
                    <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={handleUpload} />
                  </Button>
                  {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                </Box>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Detection Rules</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Max Speed Limit (km/h)</Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Slider value={maxSpeed} onChange={(e, v) => setMaxSpeed(v)} min={50} max={400} step={10} size="small" sx={{ flex: 1 }} />
                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 40 }}>{maxSpeed}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Spike Threshold (km/h jump)</Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Slider value={spikeThreshold} onChange={(e, v) => setSpikeThreshold(v)} min={10} max={150} step={5} size="small" sx={{ flex: 1 }} />
                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35 }}>{spikeThreshold}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Zero Speed Duration (s)</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Slider value={zeroDuration} onChange={(e, v) => setZeroDuration(v)} min={5} max={120} step={5} size="small" sx={{ flex: 1 }} />
                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35 }}>{zeroDuration}s</Typography>
                  </Stack>
                </Box>
                <Divider />
                {anomalies.length > 0 && (
                  <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                      <TrendingUpIcon fontSize="small" sx={{ color: '#ef4444' }} />
                      <Typography variant="subtitle2" fontWeight={700}>{anomalies.length} Anomalies</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      {['high_speed', 'spike', 'zero_movement'].map(type => {
                        const count = anomalies.filter(a => a.type === type).length;
                        if (count === 0) return null;
                        return <Chip key={type} size="small" label={`${type.replace('_', ' ')}: ${count}`}
                          sx={{ fontWeight: 600, fontSize: '0.65rem' }} />;
                      })}
                    </Stack>
                    <TableContainer sx={{ maxHeight: '30vh' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {['#', 'Type', 'Speed', 'Severity'].map(h => (
                              <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {anomalies.slice(0, 50).map((a, i) => (
                            <TableRow key={i} sx={{ '& td': { py: 0.3, fontSize: '0.7rem' } }}>
                              <TableCell>{a.index}</TableCell>
                              <TableCell>{a.type.replace('_', ' ')}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{a.speed.toFixed(1)}</TableCell>
                              <TableCell>
                                <Chip size="small" label={a.severity}
                                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                    bgcolor: a.severity === 'high' ? '#ef444422' : '#f59e0b22',
                                    color: a.severity === 'high' ? '#ef4444' : '#f59e0b' }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </Panel>
            <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />
            <Panel defaultSize={70} minSize={40}>
              <Box sx={{ height: '100%', width: '100%' }}>
                {mapGeoJson ? (
                  <MapView mapId="speed-anomaly-map" geoJsonData={mapGeoJson} styleFunction={styleFunction} pointToLayerFn={pointToLayerFn} />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)' }}>
                    <Stack alignItems="center" spacing={2} sx={{ p: 4, textAlign: 'center' }}>
                      <TrendingUpIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                      <Typography variant="h6" color="text.secondary">Upload trip data to detect speed anomalies</Typography>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Panel>
          </PanelGroup>
        </Panel>
        <PanelResizeHandle style={{ height: '5px', background: theme.palette.divider, cursor: 'row-resize' }} />
        <Panel defaultSize={30} minSize={12}>
          <Box ref={containerRef} sx={{ height: '100%', bgcolor: 'background.paper', p: 1, overflow: 'hidden' }}>
            {speeds.length > 1 ? (
              <canvas ref={canvasRef} style={{ width: chartWidth, height: 140, borderRadius: 6 }} />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Speed chart appears here</Typography>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default SpeedAnomalyDetection;
